<template>
	<div class="quote-card">
		<QuoteCardTitle class="mb-4">
			{{ company_name || name }} Earnings
		</QuoteCardTitle>

		<!-- Earnings History Chart -->
		<div v-if="earningsData && earningsData.length > 0">
			<h3 class="text-xl font-medium text-black mb-6">Earnings History</h3>
			<div class="flex flex-col gap-2 my-6">
				<div class="flex items-center gap-2">
					<span class="estimated-earnings-circle"></span>
					<span class="text-slate-500 text-sm">Estimated</span>
				</div>
				<div class="flex items-center gap-2">
					<span class="actual-earnings-circle"></span>
					<span class="text-slate-500 text-sm">Actual</span>
				</div>
			</div>
			<div
				class="mb-8"
				style="height: 400px"
			>
				<Bar
					:data="chartData"
					:options="chartOptions"
				/>
			</div>
		</div>

		<div
			v-else
			class="py-8 border-t border-slate-100"
		>
			<p class="text-slate-500 text-base">
				We don't currently have information about {{ company_name || name }}'s
				earnings.
			</p>
		</div>

		<!-- Latest Earnings Call Summary -->
		<div
			v-if="earningsFaqData && earningsFaqData.report_earnings"
			class="mt-8 flex flex-col gap-4"
		>
			<h4 class="text-xl font-medium text-black">Latest Earnings Call Summary</h4>
			<p class="text-slate-500 text-base">
				{{ earningsFaqData.report_earnings }}
			</p>
			<p class="text-slate-500 text-base">
				{{
					earningsFaqData.latest_earnings_call_date != "TBD"
						? `The latest earnings call was on ${formatDate(
								earningsFaqData.latest_earnings_call_date
						  )}.`
						: ""
				}}
			</p>
		</div>

		<!-- Latest Earnings Call Summary -->
		<div
			v-if="earningsDates && earningsDates.event && earningsDates.event.length > 0"
			class="mt-8 flex flex-col gap-4"
		>
			<h4 class="text-xl font-medium text-black">Latest Earnings Call Summary</h4>
			<!-- <p class="text-slate-500 text-base">
				{{ earningsFaqData.report_earnings }}
			</p> -->
			<p class="text-slate-500 text-base">
				{{
					getLatestEarningsCallDate(earningsDates)
						? `The latest earnings call was on ${formatDate(
								getLatestEarningsCallDate(earningsDates)
						  )}.`
						: "No recent earnings call information available."
				}}
			</p>
		</div>

		<!-- Next Earnings Date -->
		<div
			v-if="earningsDates && earningsDates.event && earningsDates.event.length > 0"
			class="mt-8 flex flex-col gap-4"
		>
			<h4 class="text-xl font-medium text-black">
				When Is {{ company_name }}'s Next Earnings Date?
			</h4>

			<!-- Known earnings date -->
			<p
				v-if="next_earnings_date !== '' && isDateTodayOrFuture(next_earnings_date)"
				class="text-slate-500 text-base"
			>
				{{ company_name }}'s next earnings date is
				{{ formatEarningsDate(next_earnings_date) }}.
			</p>

			<!-- Unknown earnings date - soft message -->
			<p
				v-else
				class="text-slate-500 text-base"
			>
				We don't currently have information about {{ name }}'s next earnings date.
				Check back soon as earnings dates are typically announced a few weeks in
				advance.
			</p>

			<div class="overflow-auto border border-slate-200 rounded-lg scrollbar-hide">
				<table class="w-full overflow-x-auto">
					<thead class="bg-slate-50">
						<tr>
							<th
								class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
							>
								Earnings Date
							</th>
							<th
								class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
							>
								Quarter
							</th>
							<th
								class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
							>
								Fiscal Year
							</th>
							<th
								class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
							>
								Time of Day
							</th>
							<th
								class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
							>
								Day of Week
							</th>
							<th
								class="px-4 py-3 text-left font-semibold text-black border-b border-slate-200 whitespace-nowrap"
							>
								Status
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="(earning, idx) in earningsDates.event.slice(0, 8)"
							:key="idx"
							class="border-b border-slate-100"
						>
							<td class="px-4 py-3 text-slate-700">
								{{ formatDate(earning.date) }}
							</td>
							<td class="px-4 py-3 text-slate-700">
								{{ earning.quarter }}
							</td>
							<td class="px-4 py-3 text-slate-700">
								{{ earning.fiscalyear }}
							</td>
							<td class="px-4 py-3 text-slate-700">
								{{ earning.timeofday || "N/A" }}
							</td>
							<td class="px-4 py-3 text-slate-700">
								{{ getDayOfWeek(earning.date) }}
							</td>
							<td class="px-4 py-3">
								<span
									class="px-2 py-1 rounded-lg text-xs font-medium"
									:class="{
										'bg-success/[0.1] text-success': earning.type === 'A',
										'bg-[#FFF3E3] text-[#F79009]': earning.type !== 'A'
									}"
								>
									{{ earning.type === "A" ? "CONFIRMED" : "ESTIMATED" }}
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup>
import { Bar } from "vue-chartjs";
import {
	Chart,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend
} from "chart.js";
import { computed } from "vue";

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const props = defineProps({
	earningsData: {
		type: Array,
		default: () => []
	},
	earningsFaqData: {
		type: Object,
		default: () => ({})
	},
	earningsDates: {
		type: Object,
		default: () => ({})
	},
	symbol: {
		type: String,
		default: ""
	},
	name: {
		type: String,
		default: ""
	}
});

// Prepare chart data from props
const chartData = computed(() => {
	if (!props.earningsData || props.earningsData.length === 0) {
		return {
			labels: [],
			datasets: []
		};
	}

	// Map API data to chart format
	const labels = props.earningsData.map(
		(earning) => earning.period || earning.quarterEnd || "N/A"
	);
	const estimatedData = props.earningsData.map(
		(earning) =>
			parseFloat(earning.estimatedEps) || parseFloat(earning.consensusEps) || 0
	);
	const actualData = props.earningsData.map(
		(earning) => parseFloat(earning.actualEps) || 0
	);

	return {
		labels,
		datasets: [
			{
				label: "Estimated",
				backgroundColor: "rgba(59,130,246,0.5)", // blue-400
				borderRadius: 8,
				borderSkipped: false,
				data: estimatedData
			},
			{
				label: "Actual",
				backgroundColor: "rgba(59,130,246,1)", // blue-600
				borderRadius: 8,
				borderSkipped: false,
				data: actualData
			}
		]
	};
});

const chartOptions = {
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: false
		},
		tooltip: {
			mode: "index",
			intersect: false,
			callbacks: {
				title: function (context) {
					return `Period: ${context[0].label}`;
				},
				label: function (context) {
					return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`;
				}
			}
		}
	},
	scales: {
		x: {
			grid: { display: false }
		},
		y: {
			beginAtZero: true,
			grid: { color: "#e5e7eb" },

			ticks: {
				callback: function (value) {
					return "$" + value.toFixed(2);
				}
			}
		}
	},
	interaction: {
		mode: "index",
		intersect: false
	}
};

// Helper functions for date formatting
const formatDate = (dateString) => {
	if (!dateString) {
		return "N/A";
	}
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "numeric"
	});
};

const getDayOfWeek = (dateString) => {
	if (!dateString) {
		return "N/A";
	}
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", { weekday: "short" });
};

const getLatestEarningsCallDate = (earningsDates) => {
	if (
		!earningsDates ||
		!earningsDates.event ||
		earningsDates.event.length === 0
	) {
		return null;
	}

	// Look through the first few items to find one with EPS property
	for (let i = 0; i < Math.min(3, earningsDates.event.length); i++) {
		const earning = earningsDates.event[i];
		if (earning.eps !== undefined && earning.eps !== null) {
			return earning.date;
		}
	}

	return null;
};

const company_name = computed(() => {
	if (!props.earningsDates) {
		return "";
	}
	return props.earningsDates.equityinfo.longname;
});

const next_earnings_date = computed(() => {
	if (!props.earningsDates) {
		return "";
	}
	return props.earningsDates.event[0].date;
});
</script>

<style scoped>
.estimated-earnings-circle {
	width: 12px;
	height: 12px;
	background: #4383ff;
	opacity: 0.8;
	border-radius: 8px;
}

.actual-earnings-circle {
	width: 12px;
	height: 12px;
	background: #4383ff;
	border-radius: 8px;
}

.scrollbar-hide {
	/* Firefox */
	scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
	/* Chrome, Safari and Opera */
	display: none;
}
</style>
